import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import "./style.css";
import "./config/firebase"; // Initialize Firebase

// Pages
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import FarmerRegistration from "./pages/FarmerRegistration";
import MilkEntry from "./pages/MilkEntry";
import SummaryReport from "./pages/SummaryReport";
import Bill from "./pages/Bill";
import CattleFood from "./pages/CattleFood";
import PriceEntry from "./pages/PriceEntry";
import FarmerDashboard from "./pages/FarmerDashboard";

// Admin email for price entry access
const ADMIN_EMAIL = 'akxay@gmail.com';

// Navigation Component with useLocation
function Navigation({ onLogout }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isAdmin = localStorage.getItem('userEmail') === ADMIN_EMAIL;
  const loginMode = localStorage.getItem('loginMode');
  const farmerName = localStorage.getItem('farmerName');
  const farmerCode = localStorage.getItem('farmerCode');

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.mobile-menu') && !event.target.closest('.hamburger-menu')) {
        closeMobileMenu();
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    closeMobileMenu();
  }, [location]);

  return (
    <>
      <nav className="navbar">
        <div className="navbar-container">
          <span className="navbar-title">
            🐄 🥛 Dairy ERP System
            {loginMode === 'farmer' && farmerName && (
              <span style={{ fontSize: '0.8em', marginLeft: '10px', opacity: 0.8 }}>
                - {farmerName} ({farmerCode})
              </span>
            )}
          </span>
          
          {loginMode === 'admin' ? (
            // Admin Navigation
            <div className="navbar-links">
              <NavLink to="/milk-entry" className={({ isActive }) => "navbar-link" + (isActive ? " active" : "")}>Milk Entry</NavLink>
              <NavLink to="/farmers" className={({ isActive }) => "navbar-link" + (isActive ? " active" : "")}>Farmer Registration</NavLink>
              <NavLink to="/summary" className={({ isActive }) => "navbar-link" + (isActive ? " active" : "")}>Summary Report</NavLink>
              <NavLink to="/bill" className={({ isActive }) => "navbar-link" + (isActive ? " active" : "")}>Bill</NavLink>
              <NavLink to="/cattle-food" className={({ isActive }) => "navbar-link" + (isActive ? " active" : "")}>Cattle Food</NavLink>
              {isAdmin && (
                <NavLink to="/price-entry" className={({ isActive }) => "navbar-link" + (isActive ? " active" : "")}>Price Entry</NavLink>
              )}
            </div>
          ) : loginMode === 'farmer' ? (
            // Farmer Navigation (only dashboard)
            <div className="navbar-links">
              <NavLink to="/farmer-dashboard" className={({ isActive }) => "navbar-link" + (isActive ? " active" : "")}>My Dashboard</NavLink>
            </div>
          ) : (
            // No navigation for unauthenticated users
            <div className="navbar-links"></div>
          )}
          
          <button onClick={onLogout} className="navbar-logout-btn"><span>Logout</span></button>
          <div className={`hamburger-menu ${isMobileMenuOpen ? 'active' : ''}`} onClick={toggleMobileMenu}>
            <div className="hamburger-line"></div>
            <div className="hamburger-line"></div>
            <div className="hamburger-line"></div>
          </div>
        </div>
      </nav>

      <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'active' : ''}`} onClick={closeMobileMenu}></div>
      <div className={`mobile-menu ${isMobileMenuOpen ? 'active' : ''}`}>
        <div className="mobile-menu-header">
          <span className="mobile-menu-title">🐄 🥛 Menu</span>
          <button className="mobile-menu-close" onClick={closeMobileMenu}>×</button>
        </div>
        <div className="mobile-nav-links">
          {loginMode === 'admin' ? (
            // Admin Mobile Menu
            <>
              <NavLink to="/milk-entry" className={({ isActive }) => "mobile-nav-link" + (isActive ? " active" : "")} onClick={closeMobileMenu}>
                <i>🥛</i>Milk Entry
              </NavLink>
              <NavLink to="/farmers" className={({ isActive }) => "mobile-nav-link" + (isActive ? " active" : "")} onClick={closeMobileMenu}>
                <i>👨‍🌾</i>Farmer Registration
              </NavLink>
              <NavLink to="/summary" className={({ isActive }) => "mobile-nav-link" + (isActive ? " active" : "")} onClick={closeMobileMenu}>
                <i>📊</i>Summary Report
              </NavLink>
              <NavLink to="/bill" className={({ isActive }) => "mobile-nav-link" + (isActive ? " active" : "")} onClick={closeMobileMenu}>
                <i>🧾</i>Bill
              </NavLink>
              <NavLink to="/cattle-food" className={({ isActive }) => "mobile-nav-link" + (isActive ? " active" : "")} onClick={closeMobileMenu}>
                <i>🌾</i>Cattle Food
              </NavLink>
              {isAdmin && (
                <NavLink to="/price-entry" className={({ isActive }) => "mobile-nav-link" + (isActive ? " active" : "")} onClick={closeMobileMenu}>
                  <i>💰</i>Price Entry
                </NavLink>
              )}
            </>
          ) : loginMode === 'farmer' ? (
            // Farmer Mobile Menu (only dashboard)
            <NavLink to="/farmer-dashboard" className={({ isActive }) => "mobile-nav-link" + (isActive ? " active" : "")} onClick={closeMobileMenu}>
              <i>📊</i>My Dashboard
            </NavLink>
          ) : null}
        </div>
        <div className="mobile-menu-footer">
          <button onClick={() => { onLogout(); closeMobileMenu(); }} className="mobile-logout-btn">
            <span>🚪</span>Logout
          </button>
        </div>
      </div>
    </>
  );
}

function AppRoutes() {
  const loginMode = localStorage.getItem('loginMode');
  
  if (loginMode === 'farmer') {
    // Farmer sees dashboard and bill - restrict other routes
    return (
      <Routes>
        <Route path="/" element={<Navigate to="/farmer-dashboard" replace />} />
        <Route path="/farmer-dashboard" element={<FarmerDashboard />} />
        <Route path="/bill" element={<Bill />} />
        <Route path="/farmers" element={<Navigate to="/farmer-dashboard" replace />} />
        <Route path="/milk-entry" element={<Navigate to="/farmer-dashboard" replace />} />
        <Route path="/summary" element={<Navigate to="/farmer-dashboard" replace />} />
        <Route path="/cattle-food" element={<Navigate to="/farmer-dashboard" replace />} />
        <Route path="/price-entry" element={<Navigate to="/farmer-dashboard" replace />} />
        <Route path="*" element={<Navigate to="/farmer-dashboard" replace />} />
      </Routes>
    );
  }
  
  // Admin sees all pages
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/milk-entry" replace />} />
      <Route path="/farmer-dashboard" element={<Navigate to="/milk-entry" replace />} />
      <Route path="/milk-entry" element={<MilkEntry />} />
      <Route path="/farmers" element={<FarmerRegistration />} />
      <Route path="/summary" element={<SummaryReport />} />
      <Route path="/bill" element={<Bill />} />
      <Route path="/cattle-food" element={<CattleFood />} />
      <Route path="/price-entry" element={<PriceEntry />} />
      <Route path="*" element={<Navigate to="/milk-entry" replace />} />
    </Routes>
  );
}

function App() {
  const [currentView, setCurrentView] = useState('landing');
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return localStorage.getItem('isLoggedIn') === 'true';
  });

  useEffect(() => {
    // Keep landing page as default, don't auto-redirect to app
    // User must click login to access dashboard
  }, [isLoggedIn]);

  const handleShowLogin = () => setCurrentView('login');
  const handleBackToLanding = () => setCurrentView('landing');
  const handleLogin = () => { setIsLoggedIn(true); setCurrentView('app'); };
  const handleLogout = () => {
    setIsLoggedIn(false);
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userId');
    localStorage.removeItem('loginMode');
    localStorage.removeItem('farmerCode');
    localStorage.removeItem('farmerName');
    localStorage.removeItem('farmerMobile');
    localStorage.removeItem('farmerVillage');
    localStorage.removeItem('farmerId');
    setCurrentView('landing');
  };

  if (currentView === 'landing') return <LandingPage onShowLogin={handleShowLogin} />;
  if (currentView === 'login') return <Router><Login onLogin={handleLogin} onBack={handleBackToLanding} /></Router>;
  return <Router><Navigation onLogout={handleLogout} /><AppRoutes /></Router>;
}

export default App;
